import { useEffect, useRef } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { useLazyCheckoutSessionRule } from '../../graphQL/queries/checkoutSessionRule'
import type { ICheckoutSessionRuleResponse } from '../../graphQL/queries/checkoutSessionRule/types'
import {
  markRecoveryRedirect,
  selectRecoveryRedirects,
  setLastAppliedRules,
} from '../../redux/slices/checkout'
import { generateDataDogContext } from '../../utils/dataDog'
import {
  CART_CONDITION_BLOCKING_RULE_TYPES,
  getCartConditionBlockedAlert,
} from '../../utils/cartConditionBlockedAlert'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useCheckoutRuleRedirect } from './useCheckoutRuleRecovery'

const FUNCTION_NAME = 'useCheckoutRulePreflight'
const CART_CONDITION_ALERT_ACTION = 'cart_condition_blocked_alert_rendered'

/**
 * Module-scoped dedup for `cart_condition_blocked_alert_rendered` (COM-1213).
 *
 * Keyed on `(sessionId, ruleType, limit)` so each unique block reports once
 * per session. Module scope rather than a `useRef` because this hook mounts
 * on `templates/Cart.tsx` — a `useRef` resets whenever the shopper leaves the
 * cart and returns, which review of #826 pointed out would double-report the
 * same unmet rule on every revisit. Module scope survives the remount, and
 * the sessionId in the key ensures a new session starts clean without any
 * explicit clear.
 */
const reportedAlerts = new Set<string>()

/**
 * Asks the rule engine what still blocks this cart, and routes out of it.
 *
 * Mounted by `templates/Cart.tsx`, which is the earliest screen where the
 * answer is actionable and the last one before the shopper commits. It asks
 * `checkoutSessionRule` with **no** `ruleType`, so the response carries every
 * rule rather than one, and lands the whole list in `checkoutSlice` for
 * `useCheckoutRuleRecovery` and COM-1045's cart rows to read.
 *
 * **It asks only where the storefront authors a screen for some rule** — a
 * non-empty `checkoutRuleRoutes` entry. Redirecting there is the only thing
 * it can do, so without one the request is all cost: server-side it runs
 * every active rule, which can call a tenant's linked-products add-on. The
 * answer is not inert either: cart-condition rules (minimum amount, maximum
 * quantity) are enforced today and come back `blocks && !checked` whenever the
 * cart misses them. `useCheckoutRuleRedirect` stays silent about a rule with
 * no authored screen, so those never redirect or toast from here.
 *
 * Mount it once. Two instances would issue two queries and race each other's
 * write to `lastAppliedRules` — the same caution `useCheckoutRuleOptions`
 * carries.
 */
export const useCheckoutRulePreflight = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const redirectToUnmetRule = useCheckoutRuleRedirect()
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  /*
   * The cart's content signature (COM-1213). A primitive that moves whenever
   * the server-pre-flight verdict could change — totals, item count, group
   * count — so the effect below re-asks the server after a successful cart
   * mutation.
   *
   * Review of #826 called this out as the primary gap: without a cart-side
   * dep, a shopper who entered below the minimum and added items past it kept
   * the stale blocking alert, and one who entered above and dropped below
   * never saw it. `onCartResponse` writes the fresh cart totals synchronously
   * on every mutation, so a selector-level primitive is the simplest edge
   * that re-runs the effect once per settled cart write.
   *
   * ⚠️ A compound string, not an object identity. `useAppSelector` compares
   * by `===`, and a new object every render would re-fetch every render. A
   * primitive is stable while the fields are, and comparable without a
   * custom equality function.
   */
  const cartSignature = useAppSelector(
    (state) =>
      `${state.cartSlice?.total ?? 0}|${state.cartSlice?.itemsTotal ?? 0}|${
        state.cartSlice?.items?.length ?? 0
      }|${state.cartSlice?.groups?.length ?? 0}`,
  )
  /*
   * `some(Boolean)`, not a key count: an authored-but-empty entry resolves to
   * no path, so it gives the redirect nowhere to go either. The object check
   * is because the map is remote JSON: `Object.values` of a string or an
   * array would turn the gate on with nothing behind it.
   */
  const routesAuthored = useAppSelector((state) => {
    const map: unknown = state.defaultSlice.config?.checkoutRuleRoutes
    return (
      typeof map === 'object' &&
      map !== null &&
      !Array.isArray(map) &&
      Object.values(map).some(Boolean)
    )
  })

  /*
   * Entry vs refresh (COM-1213, round-2 review of #826).
   *
   * The pre-flight now re-fires on cart edits via `cartSignature`, but
   * `redirectToUnmetRule` must stay **entry-only**: a refresh that returns a
   * previously-unlatched picker rule still unmet would otherwise navigate away
   * from an already-open cart. `recoveryRedirects` latches per rule type on
   * successful redirect, not per hook mount, so it does not distinguish the
   * two by itself — a picker rule the shopper satisfied at entry (never
   * latched) and then undid via a cart edit would re-latch nothing and
   * trigger the auto-redirect.
   *
   * `shouldRedirectOnNextResponseRef` is set when the entry deps move
   * (`routesAuthored`, `sessionId`, `storeName` — plus the first mount) and
   * cleared when only `cartSignature` moved. `onCompleted` reads it, calls
   * `redirectToUnmetRule` only when set, and clears it after use. Keeping
   * this as a ref (not a flag in state) means the effect does not re-run on
   * the write.
   */
  const shouldRedirectOnNextResponseRef = useRef<boolean>(true)
  /*
   * `null` sentinel for the pre-first-run snapshot, so the first dispatch
   * compares `null !== <real>` and reads as entry — this hook's first
   * meaningful fetch, by construction.
   */
  const prevEntryDepsRef = useRef<{
    sessionId: string | undefined
    storeName: string | undefined
    routesAuthored: boolean
  } | null>(null)

  /**
   * Stores every rule the engine named, then routes out of the first that
   * blocks and has a screen behind it — only on cart entry.
   *
   * `rules ?? []` rather than a bail-out: the field is nullable on the schema.
   * Writing `[]` is what separates "asked, nothing blocks" from the
   * `undefined` that means no pre-flight has run — a distinction COM-1045's
   * CTA needs and `resetSelectionsForNewSession` preserves.
   */
  const onCompleted = (response: ICheckoutSessionRuleResponse) => {
    const rules = response.checkoutSessionRule?.rules ?? []
    dispatch(setLastAppliedRules(rules))

    /*
     * One `cart_condition_blocked_alert_rendered` action per unique
     * `(sessionId, ruleType, limit)` tuple on this hook instance (COM-1213).
     *
     * Labelled `reason: 'live'` per the COM-736 convention: a zero on the
     * counter means "no cart-condition block in the window", not "signal
     * broken". The selector gate is what makes this a measurement of what the
     * shopper actually sees — a server block with no authored copy stays silent
     * everywhere.
     *
     * Firing from `onCompleted` keeps the side effect out of the render path
     * and ties the signal to the pre-flight response, which is where the data
     * lands.
     */
    const alert = getCartConditionBlockedAlert(store.getState())
    if (
      alert.show &&
      alert.ruleType &&
      CART_CONDITION_BLOCKING_RULE_TYPES.has(alert.ruleType)
    ) {
      const key = `${sessionId ?? ''}|${alert.ruleType}|${alert.limit ?? ''}`
      if (!reportedAlerts.has(key)) {
        reportedAlerts.add(key)
        const context = generateDataDogContext({
          title: 'cart-condition rule blocking at cart entry',
          extraInfo: {
            function: FUNCTION_NAME,
            reason: 'live',
            ruleType: alert.ruleType,
            limit: alert.limit,
            source: 'lastAppliedRules',
          },
        })
        datadogRum.addAction(CART_CONDITION_ALERT_ACTION, context)
      }
    }

    /*
     * The loop guard, read fresh from the store rather than from a render
     * snapshot.
     *
     * This callback runs a round trip after its closure was built, and the
     * value it needs — which rule types this session has already been sent to
     * — is written by the very redirect it is about to perform. A
     * `useAppSelector` snapshot would be as old as the request. COM-974's
     * rule, and the same reason `useCheckoutRuleSelection` reads its rollback
     * state this way.
     *
     * ⚠️ **Without this filter the shopper cannot reach their cart.** Entry
     * redirects to the picker, the picker's `back` is `useDynamicBack` —
     * `navigate(-1)` — which lands on the cart, which redirects again.
     * COM-1046's *Continuar* CTA accepts that loop deliberately because it
     * needs a tap each time round — it navigates from the handler behind the
     * button and never from an effect, so a shopper who stops tapping stops
     * moving; this one needs nothing. The second entry
     * therefore leaves the shopper on the cart, with
     * `hasUnmetBlockingRules` still `true` for that CTA to act on.
     */
    if (!shouldRedirectOnNextResponseRef.current) return
    shouldRedirectOnNextResponseRef.current = false

    const alreadySent = selectRecoveryRedirects(store.getState())
    const unmet = rules
      .filter((rule) => rule.blocks && !rule.checked)
      .map((rule) => rule.ruleType)
      .filter((ruleType) => !alreadySent.includes(ruleType))

    const sentTo = redirectToUnmetRule({ types: unmet })
    if (sentTo) dispatch(markRecoveryRedirect(sentTo))
  }

  /**
   * Reports and stops there.
   *
   * **No toast and no `manageErrors`**, deliberately departing from
   * `useGetCart`'s three-part failure shape. Nothing on the cart waits on this
   * answer: the shopper asked to see their cart and their cart is intact, so a
   * background read that failed is not their problem to act on. An error
   * screen over a working cart would be strictly worse than the silence.
   */
  const onError = (error: ApolloError) => {
    const context = generateDataDogContext({
      title: 'checkoutSessionRule pre-flight threw on cart entry',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [fetchRules] = useLazyCheckoutSessionRule({ onCompleted, onError })

  /**
   * Ask on mount, and again if the session or the storefront moves beneath a
   * mounted cart, or a route map arrives under one.
   *
   * `routesAuthored` is the gate described on the hook: no screen to send the
   * shopper to, no request.
   *
   * `sessionId` is a guard as well as a dependency, for the reason
   * `useCheckoutRuleOptions` sets out: `Config` dispatches it from an effect
   * and effects run children-first, so on the commit that first renders this
   * screen it is still `undefined` here. `checkoutSessionRule` declares
   * `$sessionUid: ID!` and Apollo drops an `undefined` variable from the
   * request body. `''` is a session that exists and is unusable, so it is
   * passed through and the server's rejection reaches `onError`.
   *
   * ⚠️ **Dispatch no loader here.** `routes/index.tsx` computes
   * `isBooting = loaders.some((name) => !NON_BOOT_LOADERS.includes(name))`,
   * where `NON_BOOT_LOADERS` exempts only `CREATE_ORDER`, `WORKFLOW_STEP`
   * and `RELOAD_PAGE` (the last one also holds no push in
   * `usePlaceOrderFlow`, COM-1521),
   * and while that is set `<Routes>` registers nothing but the `path="*"`
   * catch-all — so raising one would unmount the cart this hook is mounted on
   * and delete the screen mid-read. COM-858's failure class.
   * `useValidateLinkedProducts` brackets its own call to this query with
   * `setSynchronizingCart` for the same reason; this needs neither, because
   * nothing on the screen waits for it.
   *
   * ⚠️ **No ref, and `StrictMode` is not a reason to add one.** React 18
   * double-invokes mount effects only on a concurrent root, and `index.tsx`
   * still mounts through the legacy `ReactDOM.render`. The suite is the
   * stricter environment — `@testing-library/react` renders through
   * `createRoot` — so a test wrapping this in `<StrictMode>` sees two fetches
   * where the app sees one. The `recoveryRedirects` latch is what makes a
   * second fetch harmless anyway.
   */
  useEffect(() => {
    if (!routesAuthored || sessionId === undefined) return

    /*
     * An entry fetch is one triggered by a move in `routesAuthored`,
     * `sessionId`, or `storeName` — including the very first run, where the
     * previous values differ from the initial snapshot taken alongside the
     * effect-less `sessionId`/`storeName` reads above. A move in
     * `cartSignature` alone is a refresh.
     *
     * ⚠️ **Snapshot before the fetch dispatch, not after.** `fetchRules`
     * fires the GraphQL request, whose `onCompleted` can run as early as the
     * microtask after this effect returns. Updating the previous-deps ref
     * after `fetchRules` would race the callback and let a refresh read the
     * entry snapshot.
     */
    const prev = prevEntryDepsRef.current
    const isEntry =
      prev === null ||
      prev.sessionId !== sessionId ||
      prev.storeName !== storeName ||
      prev.routesAuthored !== routesAuthored
    prevEntryDepsRef.current = { sessionId, storeName, routesAuthored }
    shouldRedirectOnNextResponseRef.current = isEntry

    fetchRules({
      variables: {
        storefrontName: storeName || '',
        sessionUid: sessionId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesAuthored, sessionId, storeName, cartSignature])
}

export default useCheckoutRulePreflight
